// Canonical model and geometry. The self-contained ES5 factory also runs in the isolated frame.
export function createFrameCore() {
  var VERSION = 2,
    MAX_ELEMENTS = 80,
    MAX_FRAMES = 10;
  var SIZES = [
    { key: "tall", label: "Tall · 9:16", width: 1080, height: 1920 },
    { key: "portrait", label: "Portrait · 4:5", width: 1080, height: 1350 },
    { key: "square", label: "Square · 1:1", width: 1080, height: 1080 },
    { key: "landscape", label: "Landscape · 4:3", width: 1440, height: 1080 },
    { key: "wide", label: "Wide · 16:9", width: 1920, height: 1080 },
  ];
  function size(key) {
    return (
      SIZES.filter(function (s) {
        return s.key === key;
      })[0] || SIZES[0]
    );
  }
  function sizeOf(frame) {
    var width = frame && frame.width != null ? Number(frame.width) : 1080;
    var height = frame && frame.height != null ? Number(frame.height) : 1920;
    var found = SIZES.filter(function (s) {
      return s.width === width && s.height === height;
    })[0];
    if (!found) throw new Error("This post size needs a newer editor.");
    return found.key;
  }
  function resize(raw, key) {
    var frame = normalize(raw),
      target = size(key);
    var scale = Math.min(
      target.width / frame.width,
      target.height / frame.height,
    );
    var dx = (target.width - frame.width * scale) / 2;
    var dy = (target.height - frame.height * scale) / 2;
    frame.elements.forEach(function (e) {
      e.x = dx + e.x * scale;
      e.y = dy + e.y * scale;
      e.width *= scale;
      e.height *= scale;
      if (e.type === "text") {
        e.props.fontSize *= scale;
        e.props.padding *= scale;
        e.props.htmlScale *= scale;
      }
    });
    frame.width = target.width;
    frame.height = target.height;
    return normalize(frame);
  }
  function id() {
    return (
      Date.now().toString(36) + "_" + Math.random().toString(36).slice(2, 11)
    );
  }
  function copy(v) {
    return JSON.parse(JSON.stringify(v));
  }
  function num(v, fallback, min, max) {
    var n = Number(v);
    if (!isFinite(n) || v == null || v === "") n = fallback;
    return Math.max(min, Math.min(max, n));
  }
  function choice(v, options, fallback) {
    return options.indexOf(v) >= 0 ? v : fallback;
  }
  function color(v, fallback) {
    return /^#[0-9a-f]{6}$/i.test(v || "") ? v : fallback;
  }
  function url(v) {
    return typeof v === "string" && /^https?:\/\/[^\s<>"']+$/i.test(v)
      ? v
      : null;
  }
  function assetUrl(v) {
    return typeof v === "string" && /^draft-asset:\/\/[a-zA-Z0-9_-]+$/.test(v)
      ? v
      : url(v);
  }
  // Only resolved local image references may reach a host Image/img component.
  // A video's source must never be used as its image poster.
  function previewURL(item, assets) {
    if (!item) return null;
    var b = item.scene ? item.scene.background : item.background || item;
    var kind = b.kind || b.type || item.kind;
    var value =
      b.coverUrl ||
      item.coverUrl ||
      (kind !== "video" ? b.url || item.backgroundUrl || item.url : null);
    if (typeof value !== "string") return null;
    if (/^draft-asset:\/\//.test(value)) {
      value = assets && assets[value];
      return typeof value === "string" &&
        /^(file:\/\/|blob:|https?:\/\/)/i.test(value)
        ? value
        : null;
    }
    return url(value);
  }
  function assetRefs(value) {
    return Array.from(
      new Set(
        (JSON.stringify(value) || "").match(
          /draft-asset:\/\/[a-zA-Z0-9_-]+/g,
        ) || [],
      ),
    );
  }
  function replaceAssets(value, replacements) {
    if (typeof value === "string")
      return value.replace(/draft-asset:\/\/[a-zA-Z0-9_-]+/g, function (ref) {
        return replacements[ref] || ref;
      });
    if (Array.isArray(value))
      return value.map(function (item) {
        return replaceAssets(item, replacements);
      });
    if (value && typeof value === "object") {
      var result = {};
      Object.keys(value).forEach(function (key) {
        result[key] = replaceAssets(value[key], replacements);
      });
      return result;
    }
    return value;
  }
  function text(v, fallback) {
    return typeof v === "string" ? v : fallback;
  }
  function mediaProps(p) {
    p = p || {};
    return {
      url: assetUrl(p.url),
      fit: choice(p.fit, ["cover", "contain", "stretch"], "cover"),
      positionX: num(p.positionX, 0.5, 0, 1),
      positionY: num(p.positionY, 0.5, 0, 1),
      zoom: num(p.zoom, 1, 1, 4),
    };
  }
  var FONTS = [
    { key: "sans", label: "Sans", css: "Arial, Helvetica, sans-serif" },
    { key: "serif", label: "Serif", css: "Georgia, 'Times New Roman', serif" },
    {
      key: "classic",
      label: "Classic",
      css: "'Times New Roman', Times, serif",
    },
    { key: "mono", label: "Mono", css: "'Courier New', Courier, monospace" },
    {
      key: "rounded",
      label: "Rounded",
      css: "'Trebuchet MS', Verdana, sans-serif",
    },
  ];
  function fontFamily(key) {
    return (
      FONTS.filter(function (f) {
        return f.key === key;
      })[0] || FONTS[0]
    ).css;
  }
  function visibleAt(e, timeMs, durationMs) {
    if (e.hidden) return false;
    if (!e.timing || !(durationMs > 0)) return true;
    return (
      timeMs >= e.timing.startMs &&
      timeMs < (e.timing.endMs == null ? durationMs : e.timing.endMs)
    );
  }
  var TRANSITIONS = [
    { key: "none", label: "None" },
    { key: "fade", label: "Fade" },
    { key: "slide-up", label: "Slide up" },
    { key: "slide-down", label: "Slide down" },
    { key: "slide-left", label: "Slide left" },
    { key: "slide-right", label: "Slide right" },
    { key: "zoom-in", label: "Zoom in" },
    { key: "zoom-out", label: "Zoom out" },
  ];
  function transition(raw) {
    raw = raw || {};
    return {
      type: choice(
        raw.type,
        TRANSITIONS.map(function (t) {
          return t.key;
        }),
        "none",
      ),
      durationMs: num(raw.durationMs, 350, 100, 2000),
    };
  }
  // A pure video-time sample: seeking/pausing/looping cannot desynchronize motion.
  function motionAt(e, timeMs, durationMs, reducedMotion) {
    var result = { opacity: 1, x: 0, y: 0, scale: 1 };
    if (!e.transitions || !(durationMs > 0)) return result;
    var start = e.timing ? e.timing.startMs : 0;
    var end = Math.min(
      durationMs,
      e.timing && e.timing.endMs != null ? e.timing.endMs : durationMs,
    );
    var span = Math.max(0, end - start);
    var enter = transition(e.transitions.enter),
      exit = transition(e.transitions.exit);
    var inMs = enter.type === "none" ? 0 : enter.durationMs;
    var outMs = exit.type === "none" ? 0 : exit.durationMs;
    var fit = Math.min(1, span / Math.max(1, inMs + outMs));
    inMs *= fit;
    outMs *= fit;
    function apply(effect, amount, isExit) {
      if (!amount || effect.type === "none") return;
      result.opacity *= 1 - amount;
      if (reducedMotion || effect.type === "fade") return;
      var direction = isExit ? 1 : -1;
      if (effect.type === "slide-up")
        result.y -= e.height * 0.25 * amount * direction;
      if (effect.type === "slide-down")
        result.y += e.height * 0.25 * amount * direction;
      if (effect.type === "slide-left")
        result.x -= e.width * 0.25 * amount * direction;
      if (effect.type === "slide-right")
        result.x += e.width * 0.25 * amount * direction;
      if (effect.type === "zoom-in")
        result.scale *= 1 + 0.2 * amount * direction;
      if (effect.type === "zoom-out")
        result.scale *= 1 - 0.2 * amount * direction;
    }
    var inProgress = inMs ? num((timeMs - start) / inMs, 1, 0, 1) : 1;
    var outProgress = outMs
      ? num((timeMs - (end - outMs)) / outMs, 0, 0, 1)
      : 0;
    apply(enter, Math.pow(1 - inProgress, 3), false);
    apply(exit, Math.pow(outProgress, 3), true);
    return result;
  }
  function element(raw) {
    raw = raw || {};
    var type = choice(raw.type, ["text", "image", "shape", "sticker"], null);
    if (!type) throw new Error("Unsupported object type.");
    var p = raw.props || {},
      props;
    if (type === "text")
      props = {
        html: text(p.html, "<p>Your text</p>"),
        fontSize: num(p.fontSize, 64, 1, 2400),
        htmlScale: num(p.htmlScale, 1, 0.01, 100),
        fontFamily: choice(
          p.fontFamily,
          FONTS.map(function (f) {
            return f.key;
          }),
          "sans",
        ),
        color: color(p.color, "#171717"),
        align: choice(p.align, ["left", "center", "right", "justify"], "left"),
        verticalAlign: choice(
          p.verticalAlign,
          ["top", "center", "bottom"],
          "top",
        ),
        padding: num(p.padding, 12, 0, 800),
        lineHeight: num(p.lineHeight, 1.35, 0.8, 3),
      };
    if (type === "image") {
      props = mediaProps(p);
      props.alt = text(p.alt, "");
    }
    if (type === "sticker") props = { text: text(p.text, "✨").slice(0, 100) };
    if (type === "shape")
      props = {
        shape: choice(p.shape, ["rect", "ellipse", "triangle", "star"], "rect"),
        fill: p.fill === "none" ? "none" : color(p.fill, "#1a82c4"),
        stroke: color(p.stroke, "#171717"),
        strokeWidth: num(p.strokeWidth, 0, 0, 40),
        cornerRadius: num(p.cornerRadius, 24, 0, 540),
      };
    return {
      id: text(raw.id, id()),
      type: type,
      name: text(raw.name, type).slice(0, 100),
      x: num(raw.x, 140, -10000, 10000),
      y: num(raw.y, 420, -10000, 10000),
      width: num(raw.width, type === "text" ? 800 : 400, 1, 10000),
      height: num(raw.height, type === "text" ? 320 : 400, 1, 10000),
      rotation: num(raw.rotation, 0, -36000, 36000) % 360,
      opacity: num(raw.opacity, 1, 0, 1),
      locked: !!raw.locked,
      hidden: !!raw.hidden,
      groupId: typeof raw.groupId === "string" ? raw.groupId : null,
      transitions: raw.transitions
        ? {
            enter: transition(raw.transitions.enter),
            exit: transition(raw.transitions.exit),
          }
        : null,
      timing: raw.timing
        ? {
            startMs: num(raw.timing.startMs, 0, 0, 59900),
            endMs:
              raw.timing.endMs == null
                ? null
                : num(raw.timing.endMs, 60000, 100, 60000),
          }
        : null,
      props: props,
    };
  }
  function normalize(raw) {
    raw = raw || {};
    if (
      raw.version !== undefined &&
      raw.version !== 1 &&
      raw.version !== VERSION
    )
      throw new Error("This frame needs a newer editor.");
    if (!Array.isArray(raw.elements || [])) throw new Error("Invalid objects.");
    if ((raw.elements || []).length > MAX_ELEMENTS)
      throw new Error("A frame supports up to 80 objects.");
    var b = raw.background || {},
      bg = mediaProps(b),
      seen = Object.create(null);
    bg.kind = choice(b.kind, ["none", "image", "video"], "none");
    bg.color = color(b.color, "#ffffff");
    bg.coverUrl = assetUrl(b.coverUrl);
    bg.durationMs =
      b.durationMs == null ? null : num(b.durationMs, 0, 0, 60000);
    bg.muted = b.muted !== false;
    if (bg.kind === "none") {
      bg.url = null;
      bg.coverUrl = null;
      bg.durationMs = null;
    }
    var canvas = size(sizeOf(raw));
    return {
      version: VERSION,
      id: text(raw.id, id()),
      width: canvas.width,
      height: canvas.height,
      background: bg,
      elements: (raw.elements || []).map(function (e) {
        var result = element(e);
        if (result.timing) {
          var duration = bg.durationMs || 60000;
          result.timing.startMs = Math.min(
            result.timing.startMs,
            Math.max(0, duration - 100),
          );
          if (result.timing.endMs != null)
            result.timing.endMs = Math.max(
              result.timing.startMs + 100,
              Math.min(duration, result.timing.endMs),
            );
        }
        if (seen[result.id]) throw new Error("Duplicate object ID.");
        seen[result.id] = true;
        return result;
      }),
    };
  }
  function blank(key) {
    var canvas = size(key);
    return normalize({ width: canvas.width, height: canvas.height });
  }
  function make(type, frame) {
    var e = element({ type: type });
    e.x = (frame.width - e.width) / 2;
    e.y = (frame.height - e.height) / 2;
    return e;
  }
  function duplicate(frame) {
    var f = copy(frame),
      groups = Object.create(null);
    f.id = id();
    f.elements.forEach(function (e) {
      e.id = id();
      if (e.groupId) {
        groups[e.groupId] = groups[e.groupId] || id();
        e.groupId = groups[e.groupId];
      }
    });
    return f;
  }
  function plain(html) {
    return text(html, "")
      .replace(/<(script|style)\b[^>]*>[\s\S]*?<\/\1>/gi, "")
      .replace(/<[^>]*>/g, " ")
      .replace(/&(?:nbsp|#160);/gi, " ")
      .replace(/&amp;/gi, "&")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }
  function hasContent(f) {
    return (
      !!f.background.url ||
      f.background.color !== "#ffffff" ||
      f.elements.some(function (e) {
        return (
          !e.hidden &&
          e.opacity > 0 &&
          (e.type !== "text" ||
            plain(e.props.html) ||
            /<(img|table|hr|input)\b/i.test(e.props.html))
        );
      })
    );
  }
  function html(f) {
    return f.elements
      .filter(function (e) {
        return e.type === "text" && !e.hidden;
      })
      .map(function (e) {
        return e.props.html;
      })
      .join("\n");
  }
  function project(raw) {
    var f = normalize(raw),
      b = f.background;
    return {
      id: f.id,
      kind: b.kind === "none" ? "html" : b.kind,
      url: b.url,
      coverUrl: b.coverUrl,
      durationMs: b.durationMs,
      bgColor: b.color,
      bgResizeMode: b.fit,
      html: html(f),
      scene: f,
    };
  }
  function fromMedia(item) {
    if (item.scene) return normalize(item.scene);
    var f = blank();
    f.id = item.id || f.id;
    f.background = mediaProps({
      url: item.url || item.backgroundUrl,
      fit: item.bgResizeMode,
    });
    f.background.kind =
      item.kind === "video" ? "video" : f.background.url ? "image" : "none";
    f.background.color = color(item.bgColor, "#ffffff");
    f.background.coverUrl = url(item.coverUrl);
    f.background.durationMs = item.durationMs || null;
    f.background.muted = true;
    if (item.html) {
      var e = make("text", f);
      e.x = 50;
      e.y = 50;
      e.width = 980;
      e.height = 1820;
      e.props.html = item.html;
      e.props.fontSize = 48;
      e.props.color = color(item.textColor, "#171717");
      e.props.align = item.hAlign || "left";
      e.props.verticalAlign = item.vAlign || "top";
      f.elements.push(e);
    }
    return normalize(f);
  }
  function bounds(elements) {
    if (elements.length === 1) {
      var e = elements[0];
      return {
        x: e.x,
        y: e.y,
        width: e.width,
        height: e.height,
        rotation: e.rotation,
      };
    }
    var xs = [],
      ys = [];
    elements.forEach(function (e) {
      var r = (e.rotation * Math.PI) / 180,
        c = Math.cos(r),
        s = Math.sin(r);
      [-1, 1].forEach(function (a) {
        [-1, 1].forEach(function (b) {
          xs.push(
            e.x +
              e.width / 2 +
              ((a * e.width) / 2) * c -
              ((b * e.height) / 2) * s,
          );
          ys.push(
            e.y +
              e.height / 2 +
              ((a * e.width) / 2) * s +
              ((b * e.height) / 2) * c,
          );
        });
      });
    });
    var x = Math.min.apply(null, xs),
      y = Math.min.apply(null, ys);
    return {
      x: x,
      y: y,
      width: Math.max.apply(null, xs) - x,
      height: Math.max.apply(null, ys) - y,
      rotation: 0,
    };
  }
  function tags(value) {
    return text(value, "")
      .split(/[,\s]+/)
      .map(function (t) {
        return t.replace(/^#+/, "").trim().slice(0, 40);
      })
      .filter(function (t, i, a) {
        return t && a.indexOf(t) === i;
      })
      .slice(0, 12);
  }
  function payload(frames, details, uid, preview) {
    if (!uid) throw new Error("Sign in before publishing.");
    if (!frames.length || frames.length > MAX_FRAMES)
      throw new Error("Use between 1 and 10 frames.");
    var normalized = frames.map(normalize);
    var canvas = sizeOf(normalized[0]);
    if (
      normalized.some(function (f) {
        return sizeOf(f) !== canvas;
      })
    )
      throw new Error("Choose one post size for all items before publishing.");
    var media = normalized.filter(hasContent).map(project);
    media.forEach(function (m) {
      if (
        m.scene.background.kind !== "none" &&
        !(preview ? assetUrl(m.url) : url(m.url))
      )
        throw new Error("A background upload is missing.");
      m.scene.elements.forEach(function (e) {
        if (e.type === "text" && e.props.html.length > 200000)
          throw new Error(
            "A text object is too large. Split it into smaller text objects.",
          );
        if (
          e.type === "image" &&
          !(preview ? assetUrl(e.props.url) : url(e.props.url))
        )
          throw new Error("An image upload is missing.");
        if (
          e.type === "text" &&
          /(?:src|href)\s*=\s*["']?\s*(?:blob|file|data|javascript):/i.test(
            e.props.html,
          )
        )
          throw new Error(
            "Text contains a local or unsupported asset URL. Upload the image again.",
          );
      });
    });
    if (!preview && assetRefs(media).length)
      throw new Error("Some media has not been uploaded yet.");
    var caption = text(details.caption, "").trim();
    if (!caption && !media.length)
      throw new Error("Add a caption or frame content first.");
    if (caption.length > 5000)
      throw new Error("Keep the caption under 5,000 characters.");
    var first = media[0],
      cover = first && (first.coverUrl || first.url);
    if (!cover && first) {
      var image = first.scene.elements.filter(function (e) {
        return e.type === "image" && !e.hidden;
      })[0];
      cover = image && image.props.url;
    }
    var post = {
      schemaVersion: 3,
      authorId: uid,
      type: choice(
        details.type,
        [
          "networking",
          "freelance",
          "productivity",
          "advice",
          "motivation",
          "stories",
        ],
        "productivity",
      ),
      visibility: choice(
        details.visibility,
        ["public", "connections", "private"],
        "public",
      ),
      tags: tags(details.tagsText),
      language: "en",
      text:
        caption ||
        plain(
          media
            .map(function (m) {
              return m.html;
            })
            .join(" "),
        ).slice(0, 280) ||
        " ",
      format: "slides",
      rich: null,
      media: media,
      coverMediaUrl: cover || null,
      likeCount: 0,
      commentCount: 0,
      saveCount: 0,
      isPinned: false,
      isDeleted: false,
    };
    if (unescape(encodeURIComponent(JSON.stringify(post))).length > 700000)
      throw new Error(
        "This post is too large. Reduce long text or the number of objects.",
      );
    return post;
  }
  function restore(raw) {
    if (
      !raw ||
      (raw.version !== 1 && raw.version !== VERSION) ||
      !Array.isArray(raw.frames) ||
      !raw.frames.length ||
      raw.frames.length > MAX_FRAMES
    )
      throw new Error("This draft has an unsupported format.");
    var frames = raw.frames.map(normalize);
    return {
      version: VERSION,
      size: sizeOf(frames[0]),
      postId: raw.postId,
      frames: frames,
      activeId: frames.some(function (f) {
        return f.id === raw.activeId;
      })
        ? raw.activeId
        : frames[0].id,
      caption: text(raw.caption, ""),
      tagsText: text(raw.tagsText, ""),
      type: raw.type || "productivity",
      visibility: raw.visibility || "public",
    };
  }
  return {
    VERSION: VERSION,
    TRANSITIONS: TRANSITIONS,
    transition: transition,
    motionAt: motionAt,
    SIZES: SIZES,
    size: size,
    sizeOf: sizeOf,
    resize: resize,
    MAX_ELEMENTS: MAX_ELEMENTS,
    MAX_FRAMES: MAX_FRAMES,
    assetRefs: assetRefs,
    previewURL: previewURL,
    FONTS: FONTS,
    fontFamily: fontFamily,
    visibleAt: visibleAt,
    replaceAssets: replaceAssets,
    preview: function (frames, details, uid) {
      return payload(frames, details, uid || "preview", true);
    },
    id: id,
    copy: copy,
    num: num,
    url: url,
    normalize: normalize,
    blank: blank,
    make: make,
    element: element,
    duplicate: duplicate,
    plain: plain,
    hasContent: hasContent,
    project: project,
    fromMedia: fromMedia,
    bounds: bounds,
    tags: tags,
    payload: payload,
    restore: restore,
  };
}
export const frameCore = createFrameCore();
export function scriptJSON(value) {
  return JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}
